import { useTranslation } from "react-i18next";import React, { useState, useEffect, useRef } from 'react';
import { APIProvider, Map, AdvancedMarker, InfoWindow, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { Truck, Store, ShoppingCart, Coffee, Fuel, Users } from 'lucide-react';
import { calculateMemberRadiusResult } from '../../utils/radiusAnalysis';
import RadiusMembersDialog from './RadiusMembersDialog';
// Zone colors by category
const ZONE_COLORS = {
  warehouse: { fill: '#22c55e40', stroke: '#16a34a' },
  restricted: { fill: '#ef444440', stroke: '#dc2626' },
  delivery: { fill: '#f59e0b40', stroke: '#d97706' },
  service: { fill: '#3b82f640', stroke: '#2563eb' }
};

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const isValidCoordinate = (lat, lng) => {
  const latitude = toFiniteNumber(lat);
  const longitude = toFiniteNumber(lng);
  if (latitude === null || longitude === null) return false;
  if (latitude === 0 && longitude === 0) return false;
  return latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;
};

const normalizeLocation = (item = {}) => {
  if (item.location && typeof item.location.lat === 'function' && typeof item.location.lng === 'function') {
    const lat = item.location.lat();
    const lng = item.location.lng();
    return isValidCoordinate(lat, lng) ? { lat, lng } : null;
  }

  if (item.location) {
    const lat = toFiniteNumber(item.location.lat);
    const lng = toFiniteNumber(item.location.lng);
    return isValidCoordinate(lat, lng) ? { lat, lng } : null;
  }

  const lat = toFiniteNumber(item.lat);
  const lng = toFiniteNumber(item.lng);
  return isValidCoordinate(lat, lng) ? { lat, lng } : null;
};

const PlacesLayer = ({ placesTypes = [], onMarkerSelect }) => {
  const map = useMap();
  const placesLib = useMapsLibrary('places');
  const [placesService, setPlacesService] = useState(null);
  const [placesMarkers, setPlacesMarkers] = useState([]);

  useEffect(() => {
    if (!placesLib || !map) return;
    setPlacesService(new placesLib.PlacesService(map));
  }, [placesLib, map]);

  useEffect(() => {
    if (!placesService || !placesTypes || placesTypes.length === 0) {
      setPlacesMarkers([]);
      return;
    }

    const fetchAll = async () => {
      let results = [];
      const center = map.getCenter();
      if (!center) return;
      
      for (const type of placesTypes) {
        if (type === 'clients' || type === 'members') continue;
        
        const request = {
          location: center,
          radius: 5000,
          type: type
        };

        try {
          const res = await new Promise((resolve) => {
            placesService.nearbySearch(request, (data, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                resolve(data);
              } else {
                resolve([]);
              }
            });
          });
          
          results = [...results, ...res.map(r => ({ ...r, searchType: type }))];
        } catch (e) {
          console.error(e);
        }
      }
      setPlacesMarkers(results);
    };

    const listener = map.addListener('idle', fetchAll);
    fetchAll();

    return () => {
      window.google.maps.event.removeListener(listener);
    };
  }, [placesService, placesTypes, map]);

  const typeIcons = {
    bakery: { Icon: Coffee, color: '#f59e0b' },
    grocery_or_supermarket: { Icon: ShoppingCart, color: '#0ea5e9' },
    convenience_store: { Icon: Store, color: '#8b5cf6' },
    gas_station: { Icon: Fuel, color: '#ef4444' }
  };

  return (
    <>
      {placesMarkers.map((place, i) => {
        const typeInfo = typeIcons[place.searchType] || { Icon: Store, color: '#3b82f6' };
        const IconComponent = typeInfo.Icon;
        return (
          <AdvancedMarker
            key={`${place.place_id}-${i}`}
            position={place.geometry.location}
            onClick={() => onMarkerSelect?.({
              name: place.name,
              location: { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() },
              type: 'place',
              customInfo: place.vicinity,
              photoUrl: place.photos && place.photos.length > 0 ? place.photos[0].getUrl({ maxWidth: 300, maxHeight: 200 }) : null
            })}
          >
            <div
              className="cursor-pointer transform transition-all hover:scale-110"
              style={{
                width: '30px',
                height: '30px',
                background: typeInfo.color,
                borderRadius: '50%',
                border: '2px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <IconComponent className="h-4 w-4 text-white" />
            </div>
          </AdvancedMarker>
        );
      })}
    </>
  );
};

const buildClientDetails = (client) => [
  client.category ? `${client.category} Partner` : 'Partner',
  client.address,
  client.website,
  client.email
].filter(Boolean).join('\n');

const ClientMarkersLayer = ({ clientsData = [], enabled = false, onMarkerSelect }) => {
  const map = useMap();
  const placesLib = useMapsLibrary('places');
  const [clientMarkers, setClientMarkers] = useState([]);

  useEffect(() => {
    if (!enabled) {
      setClientMarkers([]);
      return;
    }

    const directClients = clientsData
      .map((client, index) => {
        const location = normalizeLocation(client);
        return location ? { ...client, location, markerKey: `client-direct-${index}` } : null;
      })
      .filter(Boolean);

    const clientsNeedingLookup = clientsData.filter((client) => !normalizeLocation(client));
    if (!placesLib || !map || clientsNeedingLookup.length === 0) {
      setClientMarkers(directClients);
      return;
    }

    let cancelled = false;
    const placesService = new placesLib.PlacesService(map);
    const lookupClient = (client, index) => new Promise((resolve) => {
      const query = [
        client.name,
        client.address,
        client.website,
        'Toronto'
      ].filter(Boolean).join(' ');

      placesService.findPlaceFromQuery(
        {
          query,
          fields: ['name', 'geometry', 'formatted_address', 'place_id', 'photos'],
          locationBias: {
            radius: 50000,
            center: { lat: 43.6532, lng: -79.3832 }
          }
        },
        (results, status) => {
          const place = status === window.google.maps.places.PlacesServiceStatus.OK && results?.[0];
          const placeLocation = place?.geometry?.location;
          if (!placeLocation) {
            resolve(null);
            return;
          }

          const location = {
            lat: placeLocation.lat(),
            lng: placeLocation.lng()
          };
          if (!isValidCoordinate(location.lat, location.lng)) {
            resolve(null);
            return;
          }

          resolve({
            ...client,
            location,
            address: client.address || place.formatted_address,
            placeId: place.place_id,
            photoUrl: place.photos && place.photos.length > 0
              ? place.photos[0].getUrl({ maxWidth: 300, maxHeight: 200 })
              : client.photoUrl,
            markerKey: `client-places-${place.place_id || index}`,
            resolvedBy: 'google_places'
          });
        }
      );
    });

    Promise.all(clientsNeedingLookup.map(lookupClient)).then((resolvedClients) => {
      if (cancelled) return;
      setClientMarkers([...directClients, ...resolvedClients.filter(Boolean)]);
    });

    return () => {
      cancelled = true;
    };
  }, [clientsData, enabled, map, placesLib]);

  if (!enabled) return null;

  return (
    <>
      {clientMarkers.map((client, i) => (
        <AdvancedMarker
          key={client.markerKey || `client-${i}`}
          position={client.location}
          onClick={() => onMarkerSelect?.({
            name: client.name,
            location: client.location,
            type: 'partner',
            status: client.status,
            customInfo: buildClientDetails(client),
            photoUrl: client.photoUrl
          })}
        >
          <div
            className="cursor-pointer transform transition-all hover:scale-125"
            style={{
              width: '32px',
              height: '32px',
              background: '#16a34a',
              borderRadius: '50%',
              border: '3px solid white',
              boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10
            }}
          >
            <Store className="h-4 w-4 text-white" />
          </div>
        </AdvancedMarker>
      ))}
    </>
  );
};

const geocodeCacheKey = (query) => `ezerp-map-geocode:${String(query || '').trim().toLowerCase()}`;

const getCachedGeocode = (query) => {
  if (!query || typeof window === 'undefined') return null;
  try {
    const cached = localStorage.getItem(geocodeCacheKey(query));
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

const setCachedGeocode = (query, location) => {
  if (!query || !location || typeof window === 'undefined') return;
  try {
    localStorage.setItem(geocodeCacheKey(query), JSON.stringify(location));
  } catch {
    // Geocode cache is best-effort only.
  }
};

const PostalClusterMarker = ({ marker }) => {
  const value = Number(marker.value) || 0;
  const size = value >= 10 ? 46 : value >= 3 ? 42 : 38;

  return (
    <div
      className="cursor-pointer transform transition-all hover:scale-110"
      title={`${marker.name}: ${value.toLocaleString()} members`}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        background: marker.color || '#2563eb',
        borderRadius: '9999px',
        border: '3px solid white',
        boxShadow: '0 3px 12px rgba(37,99,235,0.45)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: value >= 10 ? '13px' : '14px',
        fontWeight: 800,
        lineHeight: 1
      }}
    >
      {value.toLocaleString()}
    </div>
  );
};

const GeocodedMarkersLayer = ({ markers = [], onMarkerSelect, onMarkersResolved }) => {
  const map = useMap();
  const [resolvedMarkers, setResolvedMarkers] = useState([]);
  const geocodeCandidates = markers.filter((marker) => !normalizeLocation(marker) && marker.geocodeQuery);
  const geocodeSignature = geocodeCandidates
    .map((marker) => `${marker.id || marker.name}:${marker.geocodeQuery}:${marker.value || ''}`)
    .join('|');

  useEffect(() => {
    if (!map || !window.google) {
      setResolvedMarkers([]);
      onMarkersResolved?.([]);
      return;
    }

    const markersNeedingGeocode = geocodeCandidates;
    if (markersNeedingGeocode.length === 0) {
      setResolvedMarkers([]);
      onMarkersResolved?.([]);
      return;
    }

    let cancelled = false;
    const geocoder = new window.google.maps.Geocoder();

    const resolveMarkers = async () => {
      const resolved = [];

      for (const marker of markersNeedingGeocode) {
        if (cancelled) return;

        const cached = getCachedGeocode(marker.geocodeQuery);
        if (cached && isValidCoordinate(cached.lat, cached.lng)) {
          resolved.push({ ...marker, location: cached, resolvedBy: marker.resolvedBy || 'google_geocode_cache' });
          setResolvedMarkers([...resolved]);
          onMarkersResolved?.([...resolved]);
          continue;
        }

        const location = await new Promise((resolve) => {
          geocoder.geocode({ address: marker.geocodeQuery }, (results, status) => {
            const resultLocation = status === 'OK' && results?.[0]?.geometry?.location;
            if (!resultLocation) {
              resolve(null);
              return;
            }
            resolve({ lat: resultLocation.lat(), lng: resultLocation.lng() });
          });
        });

        if (cancelled) return;
        if (location && isValidCoordinate(location.lat, location.lng)) {
          setCachedGeocode(marker.geocodeQuery, location);
          resolved.push({ ...marker, location, resolvedBy: 'google_geocode' });
          setResolvedMarkers([...resolved]);
          onMarkersResolved?.([...resolved]);
        }

        await new Promise((resolve) => setTimeout(resolve, 80));
      }
    };

    resolveMarkers();

    return () => {
      cancelled = true;
    };
  // Geocoding must restart only when marker identity/query/value changes, not on every render-created array reference.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geocodeSignature, map, onMarkersResolved]);

  return (
    <>
      {resolvedMarkers.map((marker, i) => {
        const IconComponent = marker.icon || Users;
        return (
          <AdvancedMarker
            key={marker.id || `geocoded-${i}`}
            position={marker.location}
            zIndex={marker.type === 'postal_cluster' ? 40 : 20}
            onClick={() => onMarkerSelect?.(marker)}
          >
            {marker.type === 'postal_cluster' ? (
              <PostalClusterMarker marker={marker} />
            ) : (
              <div
                className="cursor-pointer transform transition-all hover:scale-110"
                style={{
                  width: '36px',
                  height: '36px',
                  background: marker.color || '#3b82f6',
                  borderRadius: '50%',
                  border: '3px solid white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <IconComponent className="h-5 w-5 text-white" />
              </div>
            )}
          </AdvancedMarker>
        );
      })}
    </>
  );
};

const PlaceInfoWindow = ({ marker, allMarkers = [], memberRadiusData = null, onCloseClick, t }) => {
  const map = useMap();
  const placesLib = useMapsLibrary('places');
  const [photoUrl, setPhotoUrl] = useState(marker.photoUrl || null);
  const [radiusKm, setRadiusKm] = useState('5');
  const [radiusResult, setRadiusResult] = useState(null);
  const radiusCircleRef = useRef(null);
  const [isRadiusOpen, setIsRadiusOpen] = useState(false);
  const [isRadiusDialogOpen, setIsRadiusDialogOpen] = useState(false);
  const markerLocation = normalizeLocation(marker);
  const markerLocationKey = markerLocation ? `${markerLocation.lat},${markerLocation.lng}` : '';
  const markerLat = markerLocation?.lat;
  const markerLng = markerLocation?.lng;
  const markerName = marker.name;
  const markerPhotoUrl = marker.photoUrl;

  useEffect(() => {
    return () => {
      if (radiusCircleRef.current) radiusCircleRef.current.setMap(null);
    };
  }, []);

  useEffect(() => {
    setRadiusKm('5');
    setRadiusResult(null);
    setIsRadiusDialogOpen(false);
    if (radiusCircleRef.current) {
      radiusCircleRef.current.setMap(null);
      radiusCircleRef.current = null;
    }
  }, [markerName, markerLocationKey]);

  const handleAnalyze = () => {
    const r = parseFloat(radiusKm);
    if (isNaN(r) || r <= 0) return;
    if (!markerLocation) return;
    
    if (radiusCircleRef.current) radiusCircleRef.current.setMap(null);
    
    const circle = new window.google.maps.Circle({
      map,
      center: markerLocation,
      radius: r * 1000,
      fillColor: '#ef4444',
      fillOpacity: 0.15,
      strokeColor: '#ef4444',
      strokeWeight: 2,
    });
    radiusCircleRef.current = circle;
    
    const result = calculateMemberRadiusResult({
      center: marker,
      radiusKm: r,
      memberRadiusData,
      fallbackMarkers: allMarkers
    });
    if (result) setRadiusResult(result);
  };

  useEffect(() => {
    setPhotoUrl(markerPhotoUrl || null);
    if (markerPhotoUrl) return;
    if (!placesLib || !map || !markerName) return;
    if (markerLat === undefined || markerLng === undefined) return;
    const photoLocation = { lat: markerLat, lng: markerLng };

    const placesService = new placesLib.PlacesService(map);
    const request = {
      query: markerName,
      fields: ['photos'],
      locationBias: {
        radius: 1000,
        center: photoLocation
      }
    };

    placesService.findPlaceFromQuery(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK && results && results.length > 0) {
        if (results[0].photos && results[0].photos.length > 0) {
          setPhotoUrl(results[0].photos[0].getUrl({ maxWidth: 300, maxHeight: 200 }));
        }
      }
    });
  }, [markerName, markerLat, markerLng, markerPhotoUrl, placesLib, map]);

  return (
    <InfoWindow
      position={normalizeLocation(marker)}
      onCloseClick={onCloseClick}>
      <div className="text-sm p-2 min-w-[200px] max-w-[280px]">
        {photoUrl && (
          <div className="mb-3 rounded overflow-hidden shadow-sm">
            <img src={photoUrl} alt={marker.name} className="w-full h-[140px] object-cover" />
          </div>
        )}
        <strong className="block text-base">{marker.name}</strong>
        {marker.status && <div className="mt-1">{t("status", "Status:")} {marker.status}</div>}
        {marker.speed !== undefined && <div className="mt-1">{t("speed", "Speed:")} {marker.speed} {t("kmh", "km/h")}</div>}
        {marker.driver && <div className="mt-1">{t("driver", "Driver:")} {marker.driver}</div>}
        {marker.customInfo && <div className="mt-2 text-gray-600 whitespace-pre-wrap">{marker.customInfo}</div>}
        
        {/* Radius Analysis UI (Discrete) */}
        <div className="mt-3 pt-2 border-t border-gray-100 flex flex-col items-end">
          <button 
            onClick={() => setIsRadiusOpen(!isRadiusOpen)}
            className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded-full"
            title="Radius Analysis (Members)"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
          </button>
          
          {isRadiusOpen && (
            <div className="flex gap-2 mt-2 w-full justify-end items-center">
              <div className="relative">
                <input 
                  type="number" 
                  placeholder="Radius" 
                  value={radiusKm}
                  onChange={(e) => setRadiusKm(e.target.value)}
                  className="w-24 border border-gray-300 rounded pl-2 pr-7 py-1 text-xs"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 pointer-events-none">
                  km
                </span>
              </div>
              <button 
                onClick={handleAnalyze}
                className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs transition-colors font-medium"
              >
                Analyze
              </button>
            </div>
          )}
          {radiusResult !== null && (
            <div className="bg-red-50 text-red-700 p-2 rounded text-xs mt-2 w-full space-y-1">
              <button
                type="button"
                onClick={() => setIsRadiusDialogOpen(true)}
                className="flex w-full items-center justify-between gap-3 rounded px-1 py-0.5 text-left font-bold transition-colors hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-300"
                title="Open members in radius list"
              >
                <span>{radiusResult.basis.includes('estimate') ? 'Members in radius (est.)' : 'Members in radius'}</span>
                <span>{radiusResult.members.toLocaleString()}</span>
              </button>
              <div className="flex items-center justify-between gap-3 text-red-800/80">
                <span>Postal clusters counted</span>
                <span>{radiusResult.clusters.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between gap-3 text-red-800/80">
                <span>Excluded missing location</span>
                <span>{radiusResult.excludedMissingLocation.toLocaleString()}</span>
              </div>
              <div className="pt-1 border-t border-red-100 text-red-800/80">
                Basis: {radiusResult.basis}
              </div>
            </div>
          )}
        </div>
        <RadiusMembersDialog
          open={isRadiusDialogOpen}
          onOpenChange={setIsRadiusDialogOpen}
          partnerName={marker.name}
          radiusKm={radiusKm}
          radiusResult={radiusResult}
        />
      </div>
    </InfoWindow>
  );
};

// Google Maps wrapper with zones support
const GoogleMap = ({
  apiKey,
  markers = [],
  zones = [],
  selectedMarker,
  onMarkerSelect,
  selectedZone,
  onZoneSelect,
  isDrawing = false,
  drawingType = 'circle',
  drawingPoints = [],
  onPointAdd,
  center = { lat: -23.5505, lng: -46.6333 },
  zoom = 12,
  className = '',
  placesTypes = [],
  clientsData = [],
  memberRadiusData = null
}) => {const { t } = useTranslation("wms");
  const [map, setMap] = useState(null);
  const [infoWindow, setInfoWindow] = useState(null);
  const [resolvedGeocodedMarkers, setResolvedGeocodedMarkers] = useState([]);
  const safeMarkers = markers.filter((marker) => normalizeLocation(marker));
  const safeSelectedMarker = selectedMarker && normalizeLocation(selectedMarker) ? selectedMarker : null;
  const allMarkersForAnalysis = [...safeMarkers, ...resolvedGeocodedMarkers];

  // Draw zones on map
  useEffect(() => {
    if (!map || !window.google) return;

    // Clear existing overlays
    // In a real implementation, we'd track these and clear them

    zones.forEach((zone) => {
      const colors = ZONE_COLORS[zone.category] || ZONE_COLORS.service;

      if (zone.type === 'circle' && zone.center) {
        new window.google.maps.Circle({
          map,
          center: { lat: zone.center.lat, lng: zone.center.lng },
          radius: zone.radius || 500,
          fillColor: colors.fill,
          fillOpacity: 0.3,
          strokeColor: colors.stroke,
          strokeWeight: 2,
          clickable: true
        });
      } else if (zone.type === 'polygon' && zone.points?.length > 2) {
        new window.google.maps.Polygon({
          map,
          paths: zone.points.map((p) => ({ lat: p.lat, lng: p.lng })),
          fillColor: colors.fill,
          fillOpacity: 0.3,
          strokeColor: colors.stroke,
          strokeWeight: 2,
          clickable: true
        });
      }
    });
  }, [map, zones]);

  // Handle map click for drawing
  useEffect(() => {
    if (!map || !isDrawing) return;

    const listener = map.addListener('click', (e) => {
      onPointAdd?.({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    });

    return () => {
      window.google?.maps.event.removeListener(listener);
    };
  }, [map, isDrawing, onPointAdd]);

  if (!apiKey) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`} style={{ minHeight: '400px' }}>
        <p className="text-gray-500">{t("googleMapsApi", "Google Maps API key required")}</p>
      </div>);

  }

  const statusColors = {
    online: '#22c55e',
    offline: '#9ca3af',
    moving: '#3b82f6',
    idle: '#f59e0b',
    alert: '#ef4444'
  };

  return (
    <APIProvider apiKey={apiKey}>
      <Map
        defaultCenter={center}
        defaultZoom={zoom}
        mapId="geofencing-map"
        className={className}
        style={{ minHeight: '400px' }}
        onCameraChanged={(ev) => {

          // Can track camera changes if needed
        }} gestureHandling="greedy">
        
        {/* Markers */}
        {safeMarkers.map((marker) => {
          const location = normalizeLocation(marker);

          const IconComponent = marker.icon || Truck;

          return (
            <AdvancedMarker
              key={marker.id}
              position={location}
              zIndex={marker.type === 'postal_cluster' ? 40 : 20}
              onClick={() => onMarkerSelect?.(marker)}>
              {marker.type === 'postal_cluster' ? (
                <PostalClusterMarker marker={marker} />
              ) : (
                <div
                  className="cursor-pointer transform transition-all hover:scale-110"
                  style={{
                    width: '36px',
                    height: '36px',
                    background: marker.color || statusColors[marker.status] || statusColors.online,
                    borderRadius: '50%',
                    border: '3px solid white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                  
                  <IconComponent className="h-5 w-5 text-white" />
                </div>
              )}
            </AdvancedMarker>);

        })}

        {/* Places API Layer */}
        <PlacesLayer placesTypes={placesTypes} onMarkerSelect={onMarkerSelect} />
        
        {/* Clients Layer */}
        <ClientMarkersLayer
          clientsData={clientsData}
          enabled={placesTypes.includes('clients')}
          onMarkerSelect={onMarkerSelect}
        />

        <GeocodedMarkersLayer
          markers={markers}
          onMarkerSelect={onMarkerSelect}
          onMarkersResolved={setResolvedGeocodedMarkers}
        />

        {/* Info Window for selected marker */}
        {safeSelectedMarker &&
          <PlaceInfoWindow
            marker={safeSelectedMarker}
            allMarkers={allMarkersForAnalysis}
            memberRadiusData={memberRadiusData}
            onCloseClick={() => onMarkerSelect?.(null)}
            t={t}
          />
        }
      </Map>
    </APIProvider>);

};

export default GoogleMap;
